// Streaming "Thinking…" status — sq_thinking_status_mock.html.
//
// Renders the spinning-sparkle + shimmer "Thinking…" header (while the turn is
// live) and the stack of step lines. Each `phase` socket event is a new step
// (older ones freeze, greyed, with a done dot); `update` replaces the active
// line in place. All step text comes from the backend (thinking_status_spec.md);
// the ONLY UI-side strings are the generic gap-fill reassurances, appended when
// no event has arrived for ~3.5s so the active line never looks frozen.
//
// Once the turn finishes the steps are GONE, not folded: every one is drawn while it runs, and when
// it finishes (done or an error) this draws nothing — no "N steps" toggle, no empty row — so the answer
// is the first thing under the question. That holds for every finished turn, and a saved chat never had
// steps: the engine stores none (K-FX6-2). UI3 folded them behind a toggle; the decision since is to hide
// them (FX6-F).

import React, { useEffect, useRef, useState } from "react";
import styles from "../sqAi.module.css";
import { StepIcon, Sparkle } from "./icons";

// UI-side gap-fill — generic, never implies a specific step (spec §"Gap-fill").
const FALLBACK = ["still on it", "almost there", "piecing it together", "lining up the numbers"];
const GAP_MS = 3500;

export default function ThinkingStatus({ steps, thinking, thinkingTick = 0 }) {
  const [gap, setGap] = useState(null);
  const lastEventAt = useRef(Date.now());
  const fbIdx = useRef(0);

  const liveStep = [...(steps || [])].reverse().find((s) => s.status === "live") || null;

  // Every thinking event bumps thinkingTick (phase/update/done) — reset the gap
  // clock and clear any reassurance on each, so even an in-place same-text
  // `update` counts as an event (a phrase-value dep would miss that case).
  useEffect(() => {
    lastEventAt.current = Date.now();
    setGap(null);
  }, [thinkingTick]);

  // Rotate a gentle reassurance only during genuine gaps, and only while live.
  // Keyed on the live step's id (stable) rather than the object (new each render).
  const liveStepId = liveStep ? liveStep.id : null;
  useEffect(() => {
    if (!thinking || !liveStepId) return undefined;
    const timer = setInterval(() => {
      if (Date.now() - lastEventAt.current < GAP_MS) return;
      setGap(FALLBACK[fbIdx.current++ % FALLBACK.length]);
    }, 1600);
    return () => clearInterval(timer);
  }, [thinking, liveStepId]);

  // A finished turn leaves nothing behind, with steps or without. Null, not an empty wrapper, so no blank
  // row sits above the answer.
  if (!thinking) return null;

  return (
    <div>
      <div className={styles.think}>
        <Sparkle className={styles.spark} />
        <span className={styles.thinkword}>Thinking…</span>
        <span className={styles.chev}>▾</span>
      </div>

      {steps && steps.length > 0 && (
        <div className={styles.steps}>
          {steps.map((s) => {
            const isLive = s.status === "live";
            return (
              <div key={s.id} className={`${styles.step} ${isLive ? "" : styles.stepDone}`}>
                {isLive ? (
                  <span className={styles.stepIcon}>
                    <StepIcon icon={s.icon} />
                  </span>
                ) : (
                  <span className={styles.dot} />
                )}
                <span className={styles.stepLabel}>{s.phrase}</span>
                {isLive &&
                  (gap ? (
                    <span className={styles.sub}>· {gap}</span>
                  ) : (
                    <span className={`${styles.sub} ${styles.dots}`} />
                  ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
